/**
 * GiftCardsListPage — `/admin/gift-cards` (BON-223).
 *
 * Browse, search and filter gift cards; open one for its balance, status and
 * ledger. Staff with permission can create a card from the header. Desktop gets
 * the table; phone and tablet get `GiftCardsResultsBody`'s card layout
 * (stacked / 2-col grid), same tiering as `BookingListBody`.
 */
import { useViewport } from '../../hooks/useViewport';
import { PaginationFooter } from '../audit/PaginationFooter';
import { DashboardsPage } from '../dashboards/DashboardsPage';
import { ListPageHeader } from '../shared/list-page/ListPageHeader';

import { GiftCardCreateButton } from './GiftCardCreateButton';
import { GiftCardsKpiRow } from './GiftCardsKpiRow';
import { GiftCardsListToolbar } from './GiftCardsListToolbar';
import { isGiftCardFiltered } from './giftCardsModel';
import { GiftCardsResultsBody } from './GiftCardsResultsBody';
import { PAGE_SIZE, useGiftCardListState } from './useGiftCardListState';

import '../dashboards/dashboards.css';

export default function GiftCardsListPage() {
  const s = useGiftCardListState();
  const { isDesktop, isPhone } = useViewport();
  const isTablet = !isPhone && !isDesktop;
  const filtered = isGiftCardFiltered({
    search: s.searchInput,
    status: s.status,
    source: s.source,
  });

  return (
    <DashboardsPage
      header={
        <ListPageHeader
          crumb="Customers › Gift cards"
          title="Gift cards"
          actions={<GiftCardCreateButton />}
        />
      }
    >
      {/* Hidden on phone — the operator found it crowded the card list at
          that width. Desktop and tablet keep it. */}
      {!isPhone && <GiftCardsKpiRow summary={s.summary} isPhone={isPhone} />}
      <section
        className="nb-card nb-card-tight"
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        <GiftCardsListToolbar
          searchInput={s.searchInput}
          onSearchInputChange={s.setSearchInput}
          status={s.status}
          onStatusChange={s.setStatus}
          source={s.source}
          onSourceChange={s.setSource}
          onClear={s.clear}
          total={s.total}
        />

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <GiftCardsResultsBody
            rows={s.rows}
            loading={s.loading}
            error={s.error}
            search={s.searchInput}
            filtered={filtered}
            isDesktop={isDesktop}
            isTablet={isTablet}
          />
        </div>

        {!s.error && !s.loading && s.rows.length > 0 && (
          <PaginationFooter
            offset={s.pager.offset}
            total={s.total}
            count={s.rows.length}
            currentPage={s.pager.currentPage}
            pageCount={s.pager.pageCount}
            hasPrev={s.pager.hasPrev}
            hasNext={s.pager.hasNext}
            onPrev={s.pager.prevPage}
            onNext={s.pager.nextPage}
            onPageSelect={(p) => s.pager.setOffset((p - 1) * PAGE_SIZE)}
          />
        )}
      </section>
    </DashboardsPage>
  );
}
